import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import { required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { PasswordField } from 'braid-design-system';
import React, { useState, useEffect } from 'react';

import { useAppContext } from '../../AppContext';

import translations from './.vocab';
import { passwordValidations } from './passwordValidations';
import { Tones } from './validation';

type Tone = 'neutral' | 'critical' | 'positive';
interface Props<FormValues> {
  forbiddenString: string;
  useField: UseField<FormValues, Language>;
  autoFocus?: boolean;
  onReset?: VoidFunction;
  setIsPasswordValid?: (isValid: boolean) => void;
  formError?: boolean;
  resetRegistrationForm?: VoidFunction;
}

export interface FieldState {
  tone: Tone;
  message: string;
  formError?: boolean;
  resetRegistrationForm?: VoidFunction;
}

const NewPassword = <FormValues extends Record<string, any>>({
  useField,
  forbiddenString,
  autoFocus = false,
  onReset = () => {},
  setIsPasswordValid = () => {},
  formError = false,
  resetRegistrationForm,
}: Props<FormValues>) => {
  const { t } = useTranslations(translations);
  const { tenant } = useAppContext();
  const [fieldState, setFieldState] = useState<FieldState>({
    tone: 'neutral',
    message: '',
  });

  const password = useField({
    id: 'newPassword',
    validators: [required],
  });

  const validationCaller = React.useCallback(
    async (passwordValue: string) => {
      setIsPasswordValid(false);

      const validationsResponse = passwordValidations(
        passwordValue,
        forbiddenString,
        t,
      );

      const isValidationFailure = validationsResponse.tone === Tones.Critical;

      if (!isValidationFailure) {
        setIsPasswordValid(true);
      }

      // Without this IF condition the component will go into a loop
      if (validationsResponse.message !== fieldState.message) {
        setFieldState(validationsResponse);
      }
    },
    [forbiddenString, setIsPasswordValid, t, fieldState],
  );

  useEffect(() => {
    if (forbiddenString) {
      validationCaller(password?.value);
    }
  }, [forbiddenString, password?.value, validationCaller]);

  const onChange = async (e: React.ChangeEvent<any>) => {
    onReset();
    if (resetRegistrationForm && formError) {
      resetRegistrationForm();
    }
    password.onChange(e);
    await validationCaller(e?.target?.value);
  };

  const passwordMessage = () => {
    if (!password.valid) {
      return <span>{password.errorMessage}</span>;
    } else if (!formError) {
      return <span>{fieldState.message}</span>;
    }
  };

  return (
    <div>
      <PasswordField
        id="newPassword"
        name={`newPassword_${tenant}`}
        autoComplete={`${tenant} new-password`}
        autoFocus={autoFocus}
        label={t('Password')}
        tone={password.errorMessage ? 'critical' : fieldState.tone}
        message={passwordMessage()}
        value={password.value}
        onChange={onChange}
        onBlur={password.onBlur}
        reserveMessageSpace
        visibilityToggleLabel={t('Toggle password visibility')}
      />

      {/* Used for testing only */}
      <span style={{ display: 'none' }} data-testid={fieldState.tone}>
        .
      </span>
    </div>
  );
};

export default NewPassword;
